import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { App, Empty } from "antd";
import { listActivity } from "@/api/activity";
import { listApps } from "@/api/apps";
import { env } from "@/config/env";
import { isMockSpaceId, resolveOwnerSpaceId } from "@/lib/currentOrg";
import { parseWorkbenchEntry, stripWorkbenchEntry } from "@/lib/entry";
import { useAppStore, useUserStore, useVersionDrawerStore } from "@/store";
import type { VersionStatus } from "@/types";
import DeveloperIdentityCard from "./DeveloperIdentityCard";
import RecentActivitySection from "./RecentActivitySection";
import RecentActivityDrawer from "./RecentActivityDrawer";
import AppListToolbar from "./AppListToolbar";
import AppCard, { AppListSkeleton } from "./AppCard";
import FirstVisitGuideModal from "./FirstVisitGuideModal";
import CreateAppModal from "./CreateAppModal";
import { toWorkbenchAppCard } from "./mapAppCard";
import { activityDestination } from "./activityTarget";
import { dismissGuide, isGuideDismissed } from "./mockActivity";
import { toActivityItem } from "./mapActivity";
import {
  isCurrentWorkbenchFailure,
  isOwnedWorkbenchSnapshot,
  workbenchAppsQueryKey,
  workbenchBlockPhase,
} from "./listPhase";
import type {
  ActivityItem,
  WorkbenchAppCard,
  WorkbenchAppFilter,
} from "./types";

const PAGE_SIZE = 20;
const ACTIVITY_PREVIEW_SIZE = 5;

type ActivitySnapshot = {
  userId: string;
  spaceId: string;
  items: ActivityItem[];
};
type AppsSnapshot = {
  userId: string;
  key: string;
  apps: WorkbenchAppCard[];
  total: number;
};

/** 离开工作台再回来时还在，用来静默刷新；整页刷新或换账号后不再沿用。 */
let activityCache: ActivitySnapshot | null = null;
let appsCache: AppsSnapshot | null = null;

/** 工具栏筛选 → 列表接口 status；「全部」不传 */
function filterToStatus(filter: WorkbenchAppFilter): VersionStatus | undefined {
  if (filter === "pending") return "scheduled";
  if (filter === "published") return "released";
  return undefined;
}

function Workbench() {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const pageLanguage = i18n.resolvedLanguage ?? i18n.language;
  const navigate = useNavigate();
  const location = useLocation();
  const syncListApps = useAppStore((s) => s.syncListApps);
  const openVersion = useVersionDrawerStore((s) => s.openVersion);
  const setSpaceId = useUserStore((s) => s.setSpaceId);
  const spaceId = useUserStore((s) => s.space_id);
  const spaces = useUserStore((s) => s.spaces);
  const spacesLoad = useUserStore((s) => s.spacesLoad);
  const userId = useUserStore((s) => s.user?.id);
  const spaceReady =
    spacesLoad === "ready" && Boolean(spaceId) && !isMockSpaceId(spaceId);
  const [activityOpen, setActivityOpen] = useState(false);
  const [activitySnapshot, setActivitySnapshotState] =
    useState(activityCache);
  const setActivitySnapshot = (next: ActivitySnapshot) => {
    activityCache = next;
    setActivitySnapshotState(next);
  };
  const [activityFailure, setActivityFailure] = useState<{
    userId: string;
    spaceId: string;
  } | null>(null);
  const activitySnapshotRef = useRef(activitySnapshot);
  activitySnapshotRef.current = activitySnapshot;
  const [search, setSearch] = useState("");
  const [keyword, setKeyword] = useState("");
  const [filter, setFilter] = useState<WorkbenchAppFilter>("all");
  const [page, setPage] = useState(1);
  const [appsSnapshot, setAppsSnapshotState] = useState(appsCache);
  const setAppsSnapshot = (next: AppsSnapshot) => {
    appsCache = next;
    setAppsSnapshotState(next);
  };
  const [appsFailure, setAppsFailure] = useState<{
    userId: string;
    key: string;
  } | null>(null);
  const [appsFetching, setAppsFetching] = useState(true);
  const appsSnapshotRef = useRef(appsSnapshot);
  appsSnapshotRef.current = appsSnapshot;

  // 换账号时丢掉上一份缓存，避免同一标签页里先画出别人的列表
  useEffect(() => {
    if (!userId) return;
    if (appsCache && !isOwnedWorkbenchSnapshot(appsCache.userId, userId)) {
      appsCache = null;
      setAppsSnapshotState(null);
    }
    if (
      activityCache &&
      !isOwnedWorkbenchSnapshot(activityCache.userId, userId)
    ) {
      activityCache = null;
      setActivitySnapshotState(null);
    }
  }, [userId]);

  const [guideOpen, setGuideOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  /** 仅空间后台 ?create=1 入口：创建成功后锁定首版所属空间 */
  const [lockSpaceId, setLockSpaceId] = useState<string | null>(null);

  // 搜索防抖后再打接口
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setKeyword(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  // 同一查询已有结果时留下卡片再替换。查询身份不含语言，切语言也静默。
  // spaceId：x-org-id 在 request() 里读当前空间；pageLanguage：X-Accept-Language
  useEffect(() => {
    if (!spaceReady || !spaceId || !userId) return;
    const queryKey = workbenchAppsQueryKey({ spaceId, filter, keyword, page });
    const cached = appsSnapshotRef.current;
    const silent =
      isOwnedWorkbenchSnapshot(cached?.userId, userId) &&
      cached?.key === queryKey;
    let cancelled = false;
    setAppsFetching(true);
    if (!silent) setAppsFailure(null);
    void listApps({
      keyword: keyword || undefined,
      status: filterToStatus(filter),
      page,
      page_size: PAGE_SIZE,
    })
      .then((result) => {
        if (cancelled) return;
        syncListApps(
          result.list.map((item) => ({
            id: item.id,
            name: item.name,
            icon: item.icon_url,
            module_id: item.module_id,
          })),
        );
        const cards = result.list.map((item) => toWorkbenchAppCard(item));
        setAppsSnapshot({
          userId,
          key: queryKey,
          apps: cards,
          total: result.total,
        });
        setAppsFailure(null);
      })
      .catch(() => {
        if (cancelled) return;
        if (silent) message.error(t("workbench.loadFailed"));
        else setAppsFailure({ userId, key: queryKey });
      })
      .finally(() => {
        if (!cancelled) setAppsFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    filter,
    keyword,
    page,
    pageLanguage,
    spaceId,
    spaceReady,
    syncListApps,
    userId,
  ]);

  // 同一空间已有动态时留下列表再替换（含切语言）；换空间先出骨架。
  useEffect(() => {
    if (!spaceReady || !spaceId || !userId) return;
    const cached = activitySnapshotRef.current;
    const silent =
      isOwnedWorkbenchSnapshot(cached?.userId, userId) &&
      cached?.spaceId === spaceId;
    let cancelled = false;
    if (!silent) setActivityFailure(null);
    void listActivity({ page: 1, page_size: ACTIVITY_PREVIEW_SIZE })
      .then((result) => {
        if (cancelled) return;
        setActivitySnapshot({
          userId,
          spaceId,
          items: result.list.map(toActivityItem),
        });
        setActivityFailure(null);
      })
      .catch(() => {
        if (cancelled) return;
        if (silent) message.error(t("workbench.activityLoadFailed"));
        else setActivityFailure({ userId, spaceId });
      });
    return () => {
      cancelled = true;
    };
  }, [pageLanguage, spaceId, spaceReady, userId]);

  const appsQueryKey =
    spaceId == null
      ? null
      : workbenchAppsQueryKey({ spaceId, filter, keyword, page });
  const appsSettled =
    appsQueryKey != null &&
    isOwnedWorkbenchSnapshot(appsSnapshot?.userId, userId) &&
    appsSnapshot?.key === appsQueryKey;
  const visibleApps = appsSettled && appsSnapshot ? appsSnapshot.apps : [];
  const visibleTotal = appsSettled && appsSnapshot ? appsSnapshot.total : 0;
  const appsPhase = workbenchBlockPhase({
    spacesLoad,
    spaceReady,
    settled: appsSettled,
    error: isCurrentWorkbenchFailure(
      appsFailure?.userId,
      appsFailure?.key,
      userId,
      appsQueryKey,
    ),
    hasItems: visibleApps.length > 0,
  });
  const activitySettled =
    spaceId != null &&
    isOwnedWorkbenchSnapshot(activitySnapshot?.userId, userId) &&
    activitySnapshot?.spaceId === spaceId;
  const visibleActivities =
    activitySettled && activitySnapshot ? activitySnapshot.items : [];
  const activityPhase = workbenchBlockPhase({
    spacesLoad,
    spaceReady,
    settled: activitySettled,
    error: isCurrentWorkbenchFailure(
      activityFailure?.userId,
      activityFailure?.spaceId,
      userId,
      spaceId ?? null,
    ),
    hasItems: visibleActivities.length > 0,
  });

  // 空间列表确定后，账号在所有空间都没有应用才弹出；关闭按账号记住
  useEffect(() => {
    if (!spaceReady || !spaceId || !userId) {
      setGuideOpen(false);
      return;
    }
    if (!appsSettled || appsFetching || appsPhase === "error") return;
    const unfilteredEmpty = !keyword && filter === "all" && visibleTotal === 0;
    if (!unfilteredEmpty || isGuideDismissed(userId)) {
      setGuideOpen(false);
      return;
    }

    const otherSpaceIds = spaces
      .map((space) => space.id)
      .filter((id) => id !== spaceId && !isMockSpaceId(id));
    if (otherSpaceIds.length === 0) {
      setGuideOpen(true);
      return;
    }

    let cancelled = false;
    void Promise.all(
      otherSpaceIds.map((orgId) => listApps({ page: 1, page_size: 1, orgId })),
    )
      .then((results) => {
        if (cancelled) return;
        setGuideOpen(results.every((result) => result.total === 0));
      })
      .catch(() => {
        if (!cancelled) setGuideOpen(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    appsFetching,
    appsPhase,
    appsSettled,
    filter,
    keyword,
    spaceId,
    spaceReady,
    spaces,
    visibleTotal,
    userId,
  ]);

  // 三端入口：消费 space_id / create，写入会话后从地址栏去掉，避免刷新重复弹窗
  useEffect(() => {
    const entry = parseWorkbenchEntry(location.search);
    if (!entry.space_id && !entry.create) return;

    if (entry.space_id) setSpaceId(entry.space_id);
    if (entry.create) {
      const space_id =
        resolveOwnerSpaceId(
          entry.space_id ?? useUserStore.getState().space_id,
        ) ?? env.devOrgId?.trim();
      setLockSpaceId(space_id || null);
      setCreateOpen(true);
    }

    const nextSearch = stripWorkbenchEntry(location.search);
    navigate(
      { pathname: location.pathname, search: nextSearch, hash: location.hash },
      { replace: true },
    );
  }, [
    location.hash,
    location.pathname,
    location.search,
    navigate,
    setSpaceId,
  ]);

  const openCreate = () => {
    setCreateOpen(true);
  };

  const openActivity = (item: ActivityItem) => {
    const dest = activityDestination(item);
    setActivityOpen(false);
    if (!dest) return;
    if (dest.version_id) openVersion(item.app_id, dest.version_id);
    navigate(dest.href);
  };

  const closeGuide = () => {
    if (userId) dismissGuide(userId);
    setGuideOpen(false);
  };

  const createFromGuide = () => {
    closeGuide();
    openCreate();
  };

  const handleFilterChange = (value: WorkbenchAppFilter) => {
    setFilter(value);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(visibleTotal / PAGE_SIZE));

  return (
    <div className="flex flex-col gap-2.5">
      <DeveloperIdentityCard />

      <RecentActivitySection
        items={activityPhase === "content" ? visibleActivities : []}
        skeleton={activityPhase === "skeleton"}
        error={activityPhase === "error"}
        onViewAll={() => setActivityOpen(true)}
        onOpenItem={openActivity}
      />

      <section className="rounded-[14px] border border-[#DCDCDC] bg-white/85 p-5 shadow-[2px_2px_30px_0_rgba(0,0,0,0.05)]">
        <div className="flex flex-col gap-5">
          <AppListToolbar
            search={search}
            filter={filter}
            onSearchChange={setSearch}
            onFilterChange={handleFilterChange}
            onCreate={openCreate}
          />

          {appsPhase === "error" ? (
            <Empty description={t("workbench.loadFailed")} />
          ) : appsPhase === "skeleton" ? (
            <AppListSkeleton />
          ) : visibleApps.length === 0 ? (
            <Empty description={t("workbench.emptyApps")} />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
                {visibleApps.map((app) => (
                  <AppCard
                    key={app.id}
                    app={app}
                    onClick={() => navigate(`/apps/${app.id}/credentials`)}
                  />
                ))}
              </div>
              {visibleTotal > PAGE_SIZE && (
                <div className="flex items-center justify-end gap-3 text-[13px] text-black/60">
                  <button
                    type="button"
                    className="disabled:opacity-40"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    {t("workbench.paginationPrev")}
                  </button>
                  <span>{t("workbench.paginationPage", { page })}</span>
                  <button
                    type="button"
                    className="disabled:opacity-40"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    {t("workbench.paginationNext")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <RecentActivityDrawer
        open={activityOpen}
        spaceReady={spaceReady}
        spaceId={spaceId}
        onClose={() => setActivityOpen(false)}
        onOpenItem={openActivity}
      />

      <FirstVisitGuideModal
        open={guideOpen}
        onClose={closeGuide}
        onCreate={createFromGuide}
      />

      <CreateAppModal
        open={createOpen}
        lockSpaceId={lockSpaceId}
        onClose={() => {
          setCreateOpen(false);
          setLockSpaceId(null);
        }}
      />
    </div>
  );
}

export default Workbench;
